import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { FormQuestionsService } from 'core/services';
import {
  QuestionBase,
  TextboxQuestion,
  HiddenQuestion,
  LinkQuestion,
  TagsQuestion,
  SwitchQuestion,
  Decorator
} from 'core/components/forms';
import { environment } from 'environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormGroup } from '@angular/forms';

@Injectable()
export class GroupEnrollmentQuestionsService implements FormQuestionsService {
  protected questions: QuestionBase<any>[];
  protected questionsSubject = new BehaviorSubject(null);
  protected formData: any = {};
  protected form: UntypedFormGroup;

  constructor(public translate: TranslateService) {}

  setForm(form: UntypedFormGroup) {
    this.form = form;
  }

  getFormData(): any {
    const value = this.form.value;
    value.validDomains = value.validDomains && value.validDomains.length ? value.validDomains.join() : '';
    return value;
  }

  onDataChanged(newData: any = {}) {
    this.formData = newData;
  }

  getQuestionsObservable() {
    return this.questionsSubject.asObservable();
  }

  getFormQuestions(params: any = {}): Observable<QuestionBase<any>[]> {
    const { group } = params;
    this.questions = [];
    this.onDataChanged();
    this.createQuestions(params);
    return this.getQuestionsObservable();
  }

  createQuestions(params: any = {}) {
    this.questions = this.getSelfRegistrationQuestions(params).filter((question) => question != null);
    this.questionsSubject.next(this.questions);
  }

  getSelfRegistrationQuestions(params: any): QuestionBase<any>[] {
    const { group, client } = params;
    let selfRegistrationUrl = '';

    if (group && group.selfRegistration) {
      const { name: groupName, clientName, gameName, startDate, endDate } = group;
      const enrollmentObject = {
        groupName,
        clientName,
        gameName,
        startDate,
        endDate,
        lang: client.lang,
        uuid: group.selfRegistration.uuid
      };
      // NOTE: btoa will fail if used with non-latin characters. It is needed to escape the string before running it
      const token = btoa(encodeURI(JSON.stringify(enrollmentObject)));
      selfRegistrationUrl = `${environment.SERVICES_URL}/enrollment?token=${token}`;
    }
    const questions = [
      new Decorator({
        html: `<span>${this.translate.instant('groups.enrollment.self.info')}</span>`,
        valueClass: 'gl-forms__text'
      }),
      new SwitchQuestion({
        key: 'enabled',
        class: 'gl-forms__position--topRight',
        switchOffLabel: 'nouns.no',
        switchOnLabel: 'nouns.yes',
        value: group && group.selfRegistration ? !!group.selfRegistration.enabled : false,
        eventHandlers: {
          onChange: (checked) => {
            const clipboardQuestion = questions.find((question) => question.key && question.key === 'copyClipboard');
            const validDomainsdQuestion = questions.find((question) => question.key && question.key === 'validDomains');
            const studentsLimitQuestion = questions.find(
              (question) => question.key && question.key === 'studentsLimit'
            );

            clipboardQuestion.setDisabled(!checked);
            validDomainsdQuestion.setDisabled(!checked);
            studentsLimitQuestion.setDisabled(!checked);
          }
        }
      }),
      new LinkQuestion({
        key: 'copyClipboard',
        class: 'col-1',
        value: selfRegistrationUrl
      }),
      new TagsQuestion({
        key: 'validDomains',
        label: 'nouns.validDomains',
        class: 'col-1',
        value:
          group && group.selfRegistration && group.selfRegistration.validDomains
            ? group.selfRegistration.validDomains.split(',')
            : []
      }),
      new TextboxQuestion({
        key: 'studentsLimit',
        label: 'nouns.studentsLimit',
        class: 'col-1',
        value: group && group.selfRegistration ? group.selfRegistration.studentsLimit : ''
      }),
      new HiddenQuestion({
        key: 'uuid',
        value: group && group.selfRegistration ? group.selfRegistration.uuid : ''
      })
    ];
    return questions;
  }
}
